import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { US_STATES, formatPhone, formatZip } from "@/lib/formHelpers";
import {
  Globe, Search, CheckCircle2, XCircle, ExternalLink, Copy, ArrowLeft,
  Plus, X, Trash2, Send,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  checking:        "bg-blue-50 border-blue-200 text-blue-700",
  intake_pending:  "bg-amber-50 border-amber-200 text-amber-800",
  intake_complete: "bg-purple-50 border-purple-200 text-purple-800",
  registered:      "bg-green-50 border-green-200 text-green-800",
  live:            "bg-emerald-100 border-emerald-300 text-emerald-900",
  cancelled:       "bg-red-50 border-red-200 text-red-700",
};

// -----------------------------------------------------------------------------
// LIST PAGE: /domains
// -----------------------------------------------------------------------------
export default function Domains() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: "", desired_names: "", admin_notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/domains/");
      setRows(data.domains || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (showCreate) {
      api.get("/clients").then(({ data }) => setClients(data.clients || [])).catch(() => {});
    }
  }, [showCreate]);

  const create = async () => {
    try {
      const desired_names = form.desired_names.split(/[\s,]+/).filter(Boolean).map((n) => n.toLowerCase());
      const { data } = await api.post("/domains/", {
        client_id: form.client_id,
        desired_names,
        admin_notes: form.admin_notes,
      });
      toast.success("Domain intake created.");
      setShowCreate(false);
      setForm({ client_id: "", desired_names: "", admin_notes: "" });
      setRows([data, ...rows]);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="domains-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Add-on services</div>
          <h1 className="text-3xl font-bold text-neutral-900">Domain registrations</h1>
          <p className="text-neutral-600 mt-2 text-sm max-w-2xl">
            Check availability, capture client intake via a shareable link, then register manually with your registrar of choice. Cost auto-adds to the client&apos;s monthly plan.
          </p>
        </div>
        <button data-testid="domains-create-btn" onClick={() => setShowCreate(true)} className="lf-btn-primary text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> New domain intake
        </button>
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
            <tr>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Selected / desired</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Registrar</th>
              <th className="text-right px-4 py-3">Monthly add-on</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-10 text-center text-neutral-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-neutral-400" data-testid="domains-empty">No domain intakes yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                <td className="px-4 py-3">
                  <Link to={`/clients/${r.client_id}`} className="font-medium hover:underline">{r.client_business_name || "(deleted)"}</Link>
                </td>
                <td className="px-4 py-3">
                  {r.selected_name ? (
                    <span className="font-mono font-semibold" data-testid={`domain-selected-${r.id.slice(0, 8)}`}>{r.selected_name}</span>
                  ) : (
                    <span className="text-xs text-neutral-500 font-mono">{(r.desired_names || []).join(", ") || "—"}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 text-xs font-mono uppercase tracking-wider border ${STATUS_COLORS[r.status] || ""}`} data-testid={`domain-status-${r.id.slice(0, 8)}`}>
                    {r.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{r.registrar || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{r.domain_addon_monthly ? `$${r.domain_addon_monthly}/mo` : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/domains/${r.id}`} className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid={`domain-manage-${r.id.slice(0, 8)}`}>
                    Manage <ExternalLink className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white w-full max-w-lg border border-neutral-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-neutral-100 flex justify-between">
              <div><div className="overline">New</div><div className="text-lg font-bold">Domain intake</div></div>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <select data-testid="domain-create-client" className="lf-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
              </select>
              <textarea data-testid="domain-create-names" className="lf-input" rows={2} placeholder="Suggested names (comma or newline separated) — e.g. bobsplumbing.com, bobs-plumbing.net" value={form.desired_names} onChange={(e) => setForm({ ...form, desired_names: e.target.value })} />
              <textarea data-testid="domain-create-notes" className="lf-input" rows={2} placeholder="Internal notes (optional)" value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} />
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="lf-btn-outline text-sm">Cancel</button>
              <button data-testid="domain-create-submit" onClick={create} disabled={!form.client_id} className="lf-btn-primary text-sm">Create intake</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// DETAIL PAGE: /domains/:id
// -----------------------------------------------------------------------------
export function DomainDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [checkNames, setCheckNames] = useState("");
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState([]);
  const [regForm, setRegForm] = useState({ registrar: "Namecheap", annual_cost: 15, expiry: "", domain_addon_monthly: 3 });
  const [registering, setRegistering] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/domains/${id}`);
      setD(data);
      if (data.selected_name) setCheckNames(data.selected_name);
      else if (data.desired_names?.length) setCheckNames(data.desired_names.join("\n"));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [id]);

  const runCheck = async () => {
    setChecking(true);
    const names = checkNames.split(/[\s,]+/).filter(Boolean).map((n) => n.toLowerCase());
    try {
      const { data } = await api.post("/domains/check", { names });
      setResults(data.results || []);
      // Also cache in the domain record
      const cache = { ...(d.availability_results || {}) };
      data.results.forEach((r) => { if (r.available !== null) cache[r.name] = r.available; });
      await api.patch(`/domains/${id}`, { availability_results: cache });
      setD({ ...d, availability_results: cache });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setChecking(false); }
  };

  const setSelected = async (name) => {
    try {
      const tld = name.split(".").pop();
      await api.patch(`/domains/${id}`, { selected_name: name, tld });
      setD({ ...d, selected_name: name, tld });
      toast.success(`Selected ${name}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const patchStatus = async (status) => {
    try {
      const { data } = await api.patch(`/domains/${id}`, { status });
      setD(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyIntakeLink = async () => {
    const url = `${window.location.origin}/domain-intake/${id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Intake link copied — send to client."); }
    catch { toast.error("Copy failed."); }
  };

  const submitRegister = async () => {
    setRegistering(true);
    try {
      const { data } = await api.post(`/domains/${id}/register`, regForm);
      setD(data);
      toast.success("Registered — client MRR bumped.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRegistering(false); }
  };

  if (!d) return <div className="p-10 text-neutral-400">Loading…</div>;

  const intakeUrl = `${window.location.origin}/domain-intake/${id}`;

  return (
    <div className="p-8 max-w-4xl" data-testid="domain-detail-page">
      <Toaster position="top-right" richColors />
      <Link to="/domains" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-black mb-6"><ArrowLeft className="w-4 h-4" /> Back to domains</Link>

      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Domain for</div>
          <h1 className="text-3xl font-bold">{d.client_business_name}</h1>
          {d.selected_name && <div className="mt-2 font-mono text-xl text-amber-700" data-testid="domain-selected-display">{d.selected_name}</div>}
        </div>
        <span className={`px-3 py-1 text-xs font-mono uppercase tracking-wider border ${STATUS_COLORS[d.status] || ""}`}>{d.status.replace(/_/g, " ")}</span>
      </div>

      {/* Shareable client intake link */}
      {d.status !== "registered" && d.status !== "live" && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <div className="text-xs font-mono uppercase tracking-widest text-amber-900 mb-1">Client intake link</div>
            <code className="text-xs break-all">{intakeUrl}</code>
          </div>
          <button onClick={copyIntakeLink} data-testid="domain-copy-intake-btn" className="lf-btn-outline text-sm flex items-center gap-2">
            <Copy className="w-4 h-4" /> Copy link
          </button>
        </div>
      )}

      {/* Availability checker */}
      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <div className="overline mb-3">Availability checker</div>
        <textarea data-testid="domain-check-input" className="lf-input mb-3" rows={3} placeholder="Enter domain names — one per line" value={checkNames} onChange={(e) => setCheckNames(e.target.value)} />
        <button data-testid="domain-check-btn" onClick={runCheck} disabled={checking || !checkNames.trim()} className="lf-btn-primary text-sm flex items-center gap-2">
          <Search className="w-4 h-4" /> {checking ? "Checking…" : "Check availability"}
        </button>

        {results.length > 0 && (
          <div className="mt-4 space-y-2">
            {results.map((r, i) => (
              <div key={i} className="flex items-center justify-between border border-neutral-200 px-3 py-2 text-sm" data-testid={`check-result-${i}`}>
                <div className="flex items-center gap-2 flex-1">
                  {r.available === true ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                    : r.available === false ? <XCircle className="w-4 h-4 text-red-600 shrink-0" />
                    : <XCircle className="w-4 h-4 text-neutral-300 shrink-0" />}
                  <span className="font-mono">{r.name}</span>
                  {r.available === true && <span className="text-xs text-green-700 font-semibold uppercase tracking-wider">available</span>}
                  {r.available === false && <span className="text-xs text-red-700 uppercase tracking-wider">taken</span>}
                  {r.error && <span className="text-xs text-neutral-400">({r.error})</span>}
                </div>
                {r.available === true && (
                  <button onClick={() => setSelected(r.name)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold uppercase" data-testid={`select-${i}`}>Select</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Intake summary if complete */}
      {d.intake && (
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <div className="overline mb-3">Client intake submitted</div>
          <table className="text-sm">
            <tbody>
              {Object.entries(d.intake).map(([k, v]) => v && k !== "selected_name" ? (
                <tr key={k}><td className="text-neutral-500 pr-4 py-1 uppercase text-xs font-mono">{k.replace(/_/g, " ")}</td><td className="py-1">{v}</td></tr>
              ) : null)}
            </tbody>
          </table>
        </div>
      )}

      {/* Register form */}
      {d.status === "intake_complete" && (
        <div className="bg-white border border-amber-300 p-6 mb-6">
          <div className="overline mb-3">Mark as registered</div>
          <p className="text-sm text-neutral-600 mb-4">Once you&apos;ve registered <code>{d.selected_name}</code> at your registrar, record the details here. Client&apos;s monthly plan will auto-bump by the add-on amount.</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input className="lf-input" placeholder="Registrar (Namecheap, Porkbun, etc.)" value={regForm.registrar} onChange={(e) => setRegForm({ ...regForm, registrar: e.target.value })} data-testid="register-registrar" />
            <input className="lf-input" type="number" step="0.01" placeholder="Annual cost" value={regForm.annual_cost} onChange={(e) => setRegForm({ ...regForm, annual_cost: parseFloat(e.target.value || 0) })} data-testid="register-annual" />
            <input className="lf-input" type="date" placeholder="Expiry" value={regForm.expiry} onChange={(e) => setRegForm({ ...regForm, expiry: e.target.value })} data-testid="register-expiry" />
            <input className="lf-input" type="number" step="0.01" placeholder="Monthly add-on to client plan" value={regForm.domain_addon_monthly} onChange={(e) => setRegForm({ ...regForm, domain_addon_monthly: parseFloat(e.target.value || 0) })} data-testid="register-monthly" />
          </div>
          <button onClick={submitRegister} disabled={registering || !regForm.expiry} className="lf-btn-primary text-sm flex items-center gap-2" data-testid="register-submit">
            <CheckCircle2 className="w-4 h-4" /> {registering ? "Saving…" : "Mark as registered"}
          </button>
        </div>
      )}

      {d.status === "registered" && (
        <div className="p-6 bg-green-50 border border-green-200">
          <div className="flex items-center gap-2 text-green-800 font-semibold mb-2"><CheckCircle2 className="w-5 h-5" /> Registered</div>
          <div className="text-sm text-green-900">
            {d.selected_name} · via {d.registrar} · expires {d.expiry} · ${d.annual_cost}/yr
          </div>
          <div className="text-sm text-green-900 mt-1">
            Client billed <strong>+${d.domain_addon_monthly}/mo</strong> as part of their plan.
          </div>
          <button onClick={() => patchStatus("live")} className="lf-btn-outline text-sm mt-3">Mark as live →</button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PUBLIC INTAKE FORM: /domain-intake/:id
// -----------------------------------------------------------------------------
export function DomainIntakePublic() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({
    selected_name: "", registrant_name: "", org: "", email: "", phone: "",
    address_1: "", address_2: "", city: "", state: "", postal: "", country: "US",
  });
  const [checkNames, setCheckNames] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/domains/public/${id}`);
        setD(data);
        setForm((f) => ({ ...f, selected_name: data.selected_name || "" }));
        setCheckNames((data.desired_names || []).join("\n"));
      } catch { setNotFound(true); }
    })();
  }, [id]);

  const runCheck = async () => {
    setBusy(true);
    const names = checkNames.split(/[\s,]+/).filter(Boolean).map((n) => n.toLowerCase());
    try {
      const { data } = await axios.post(`${BACKEND}/api/domains/public/${id}/check`, { names });
      setResults(data.results || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/domains/public/${id}/intake`, form);
      setSubmitted(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-400">Link expired.</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-400">Loading…</div>;
  if (d.status === "registered" || d.status === "live") return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-300 flex-col gap-4 p-6 text-center">
      <CheckCircle2 className="w-12 h-12 text-green-400" />
      <h1 className="text-2xl">Your domain is registered!</h1>
      <p>We&apos;ll be in touch about pointing it at your site.</p>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-300 flex-col gap-4 p-6 text-center">
      <CheckCircle2 className="w-14 h-14 text-amber-400" />
      <h1 className="text-2xl">Thanks! Your info is in.</h1>
      <p>We&apos;ll register your domain and email you when it&apos;s live — usually within one business day.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 py-14 px-4" data-testid="domain-intake-public">
      <Toaster position="top-right" richColors />
      <div className="max-w-2xl mx-auto">
        <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-2">Clients OnRamp · Domain intake</div>
        <h1 className="text-3xl font-black mb-2">Pick your domain, {d.client_business_name}</h1>
        <p className="text-neutral-400 text-sm mb-8">Search availability, pick the one you want, and give us the info we need to register it on your behalf. Cost is baked into your monthly plan — nothing extra to pay upfront.</p>

        {/* Availability */}
        <div className="border border-neutral-800 bg-neutral-900 p-6 mb-6">
          <div className="text-xs uppercase font-mono tracking-widest text-neutral-400 mb-3">1. Check availability</div>
          <textarea className="w-full bg-neutral-950 border border-neutral-700 p-3 text-sm mb-3 font-mono" rows={3} placeholder="yourbusiness.com&#10;your-business.net" value={checkNames} onChange={(e) => setCheckNames(e.target.value)} data-testid="public-check-input" />
          <button onClick={runCheck} disabled={busy || !checkNames.trim()} className="px-5 py-2 bg-amber-500 text-black font-semibold uppercase tracking-widest text-xs flex items-center gap-2" data-testid="public-check-btn">
            <Search className="w-3 h-3" /> {busy ? "Checking…" : "Check"}
          </button>
          {results.length > 0 && (
            <div className="mt-4 space-y-2">
              {results.map((r, i) => (
                <div key={i} className="flex items-center justify-between border border-neutral-800 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    {r.available === true ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                    <span className="font-mono">{r.name}</span>
                    {r.available && <span className="text-xs text-green-400 uppercase">available</span>}
                    {r.available === false && <span className="text-xs text-red-400 uppercase">taken</span>}
                  </div>
                  {r.available && (
                    <button onClick={() => setForm({ ...form, selected_name: r.name })} className={`text-xs uppercase font-semibold tracking-wider ${form.selected_name === r.name ? "text-amber-400" : "text-neutral-400 hover:text-amber-400"}`} data-testid={`public-pick-${i}`}>
                      {form.selected_name === r.name ? "Selected ✓" : "Pick this"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Intake form */}
        <div className="border border-neutral-800 bg-neutral-900 p-6 mb-6">
          <div className="text-xs uppercase font-mono tracking-widest text-neutral-400 mb-3">2. Your info (for domain registration)</div>
          {form.selected_name && <div className="mb-4 text-amber-400 font-mono">→ Registering <strong>{form.selected_name}</strong></div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Input testId="intake-name" label="Full name *" value={form.registrant_name} onChange={(v) => setForm({ ...form, registrant_name: v })} />
            <Input testId="intake-org" label="Business name" value={form.org} onChange={(v) => setForm({ ...form, org: v })} />
            <Input testId="intake-email" label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Input testId="intake-phone" label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: formatPhone(v) })} placeholder="(555) 123-4567" maxLength={14} />
            <Input testId="intake-addr1" label="Street address *" full value={form.address_1} onChange={(v) => setForm({ ...form, address_1: v })} />
            <Input testId="intake-addr2" label="Suite / Apt (optional)" full value={form.address_2} onChange={(v) => setForm({ ...form, address_2: v })} />
            <Input testId="intake-city" label="City *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <Select testId="intake-state" label="State *" value={form.state} onChange={(v) => setForm({ ...form, state: v })}
              options={[["", "— Select state —"], ...US_STATES.map(([c, n]) => [c, `${c} — ${n}`])]} />
            <Input testId="intake-postal" label="Postal code *" value={form.postal} onChange={(v) => setForm({ ...form, postal: formatZip(v) })} placeholder="78701" maxLength={10} />
            <Select testId="intake-country" label="Country *" value={form.country} onChange={(v) => setForm({ ...form, country: v })}
              options={[["US", "United States"], ["CA", "Canada"]]} />
          </div>
        </div>

        <button
          onClick={submit}
          disabled={busy || !form.selected_name || !form.registrant_name || !form.email || !form.phone || !form.address_1 || !form.city || !form.state || !form.postal}
          className="w-full py-4 bg-amber-500 text-black font-semibold uppercase tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="public-submit"
        >
          <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit intake"}
        </button>
        <p className="text-xs text-neutral-500 mt-3 text-center">By submitting you authorize Clients OnRamp to register the selected domain on your behalf using this info as WHOIS registrant data.</p>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", full, testId, placeholder, maxLength }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-neutral-400 uppercase tracking-wider font-mono">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="bg-neutral-950 border border-neutral-700 p-2.5 text-neutral-100 focus:border-amber-500 focus:outline-none"
      />
    </label>
  );
}

function Select({ label, value, onChange, testId, options, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-neutral-400 uppercase tracking-wider font-mono">{label}</span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-neutral-950 border border-neutral-700 p-2.5 text-neutral-100 focus:border-amber-500 focus:outline-none"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </label>
  );
}
